import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { UntypedFormGroup } from '@angular/forms';
import { SelectOptionLoaderI } from '../../configurable-form/components/form-select/models/select-option-loader-i';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { SelectOptionI } from '../../configurable-form/components/form-select/models/select-option-i';
import { getOpenPlatformContracts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/contracts.selectors';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { getLocalSpacings } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/single-catalogs';
import { getPumpPrimerState } from '../redux/pump-primer.selectors';
import {
  PUMP_NONE_CODE,
  PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY,
  PUMP_PRIMER_MIX,
  PUMP_PRIMER_SLUMP,
} from 'app/shared/pumping/pumping.constant';
import { getRmsRecipeSpecifications } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import { runJSQueue } from '@ordersnproducts/utils/run-js-queue';

@Injectable()
export class PumpPrimerProductLoaderService implements SelectOptionLoaderI {
  constructor(protected store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the products
   * @return {Observable<SelectOptionI[]>}
   */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getOpenPlatformContracts),
      map((availableContracts) => {
        const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
        const contractCode = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
        const contracts = availableContracts[productCode];
        const contract = _safe(() =>
          contracts.find((localContract) => localContract.contractCode.replace(/^0+/, '') === contractCode),
        ) as any;
        if (!contract) {
          return [];
        }
        const noneOption: SelectOptionI = {
          id: PUMP_NONE_CODE,
          name: 'None',
        };
        if (contract.pumpPrimerProducts.length > 0) {
          const pumpPrimerProducts = contract.pumpPrimerProducts.map(
            ({ pumpPrimerCode: id, pumpPrimerDesc: name }) => ({
              id,
              name,
            }),
          );
          return [noneOption].concat(pumpPrimerProducts);
        } else {
          const pumpPrimerMixControl = formGroup.get(PUMP_PRIMER_MIX);
          noneOption.default = true;
          runJSQueue(() => pumpPrimerMixControl.disable());
          return [noneOption];
        }
      }),
    );
  }
}

@Injectable()
export class PrimerArrivalBeforeDeliveryLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   * */
  loadOptions(formGroup: UntypedFormGroup, config: FieldConfig): Observable<SelectOptionI[]> {
    const defaultPpabd$ = this.getDefaultPpabd().pipe(take(1));
    const maxPpabd$ = this.getMaxPpabd().pipe(take(1));
    const orderPrimerArrivalBeforeDelivery$ = this.getSelectedPrimerArrivalBeforeDelivery().pipe(take(1));
    return combineLatest([defaultPpabd$, maxPpabd$, orderPrimerArrivalBeforeDelivery$]).pipe(
      switchMap(([defaultPpabd, maxPpabd, orderPrimerArrivalBeforeDelivery]) =>
        this.store.pipe(
          select(getLocalSpacings),
          filter((state) => !!state),
          map((spacings) => {
            if (!orderPrimerArrivalBeforeDelivery) {
              const defaultPrimerArrivalBeforeDelivery = spacings.find(
                (spacing) => Number(spacing.spacingCode) === defaultPpabd,
              );
              formGroup
                .get(PUMP_PRIMER_ARRIVAL_BEFORE_DELIVERY)
                .setValue(defaultPrimerArrivalBeforeDelivery.spacingCode);
            }
            if (spacings.length) {
              return spacings
                .filter((spacing) => Number(spacing.spacingCode) > 0 && Number(spacing.spacingCode) <= maxPpabd)
                .map((spacing) => {
                  return {
                    id: spacing.spacingCode,
                    name: `${spacing.spacingDesc}`,
                  };
                });
            }
            return [];
          }),
        ),
      ),
    );
  }

  /**
   * Get the default pump primer arrival before delivery
   * @return {Observable<number>}
   * */
  private getDefaultPpabd(): Observable<number> {
    return this.store
      .select(getPumpPrimerState)
      .pipe(map(({ defaultPumpPrimerArrivalBeforeDelivery }) => defaultPumpPrimerArrivalBeforeDelivery));
  }

  private getMaxPpabd(): Observable<number> {
    return this.store
      .select(getPumpPrimerState)
      .pipe(map(({ maxPumpPrimerArrivalBeforeDelivery }) => maxPumpPrimerArrivalBeforeDelivery));
  }

  /**
   * Get the selected pump primer arrival before delivery
   * @return {Observable<number>}
   * */
  public getSelectedPrimerArrivalBeforeDelivery() {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      map((recipe) => {
        return recipe[0].values.primerArrivalBeforeDelivery;
      }),
      take(1),
    );
  }

  public getPrimerArrivalDefaultValue(): Observable<string> {
    const defaultPpabd$ = this.getDefaultPpabd().pipe(take(1));
    const maxPpabd$ = this.getMaxPpabd().pipe(take(1));
    return combineLatest([defaultPpabd$, maxPpabd$]).pipe(
      switchMap(([defaultPpabd, maxPpabd]) =>
        this.store.pipe(
          select(getLocalSpacings),
          filter((state) => !!state),
          map((spacings) => {
            if (spacings.length) {
              const primerArrivals = spacings.filter(
                (spacing) => Number(spacing.spacingCode) > 0 && Number(spacing.spacingCode) <= maxPpabd,
              );
              const primerArrivalDefault = primerArrivals.find(
                (pArrival) => Number(pArrival.spacingCode) === defaultPpabd,
              );
              return primerArrivalDefault.spacingCode;
            }
            return undefined;
          }),
        ),
      ),
    );
  }
}

@Injectable()
export class PrimerSlumpLoaderService implements SelectOptionLoaderI {
  constructor(private store: Store<OrderBuilderState>) {}

  /**
   * Provides an observable of the elements
   * @return {Observable<SelectOptionI[]>}
   * */
  loadOptions(formGroup: UntypedFormGroup): Observable<SelectOptionI[]> {
    return this.store.pipe(
      select(getPumpPrimerState),
      filter((state) => !!state),
      switchMap((primerSlumps) => {
        if (!this.isValidPrimerSlumps(primerSlumps)) {
          return [];
        }

        const { primerSlumpProduct, primerSlumps: slumps } = primerSlumps;
        const sortedFilteredSlumps = this.getSortedFilteredSlumps(slumps, primerSlumpProduct);
        const defaultSlumpCode = this.getDefaultSlumpCode(sortedFilteredSlumps, primerSlumpProduct);

        return this.getSelectedPrimerSlump().pipe(
          take(1),
          map((selectedPrimerSlump) => {
            const slumpCodeToSet = selectedPrimerSlump || defaultSlumpCode;
            formGroup.get(PUMP_PRIMER_SLUMP).setValue(slumpCodeToSet);
            return this.mapToSelectOptions(sortedFilteredSlumps);
          }),
        );
      }),
    );
  }

  /*
   * Check if the primer slumps are valid
   * @param primerSlumps
   * @return {boolean}
   */
  private isValidPrimerSlumps(primerSlumps: any): boolean {
    return primerSlumps && primerSlumps.primerSlumps && primerSlumps.primerSlumpProduct;
  }

  /**
   * Get the sorted and filtered slumps
   * @param slumps
   * @param product
   * @return {any[]}
   */
  private getSortedFilteredSlumps(slumps: any[], product: any): any[] {
    return slumps
      .sort((a, b) => Number(a.slumpCode) - Number(b.slumpCode))
      .filter((slump) => Number(slump.slumpCode) >= product.slumpLow && Number(slump.slumpCode) <= product.slumpHigh);
  }

  /**
   * Get the default slump code
   * @param slumps
   * @param product
   * @return {number}
   */
  private getDefaultSlumpCode(slumps: any[], product: any): number {
    const targetSlump = slumps.find((slump) => Number(slump.slumpCode) === product.targetSlump);
    const minSlump = slumps.find((slump) => Number(slump.slumpCode) === product.slumpLow);
    return targetSlump && targetSlump.slumpCode !== undefined ? targetSlump.slumpCode : minSlump.slumpCode;
  }

  /**
   * Map the slumps to select options
   * @param slumps
   * @return {SelectOptionI[]}
   */
  private mapToSelectOptions(slumps: any[]): SelectOptionI[] {
    return slumps.map((slump) => ({
      id: slump.slumpCode,
      name: `${slump.slumpDesc}`,
    }));
  }

  /**
   * Get the selected primer slump
   * @return {Observable<number>}
   * */
  private getSelectedPrimerSlump() {
    return this.store.select(getRmsRecipeSpecifications).pipe(
      map((recipe) => {
        return _safe(() => recipe[0].values.primerSlump, undefined);
      }),
    );
  }

  public getPrimerSlumpDefaultValue(): Observable<string> {
    return this.store.pipe(
      select(getPumpPrimerState),
      filter((state) => {
        return !!state;
      }),
      map((primerSlumps) => {
        if (!primerSlumps || !primerSlumps.primerSlumps || !primerSlumps.primerSlumpProduct) {
          return undefined; // Return an empty array if primerSlumps or primerSlumps.primerSlumps is null or undefined
        }
        const slumpProduct = primerSlumps.primerSlumpProduct;
        const primerSlumpsList = primerSlumps.primerSlumps
          .sort((a, b) => Number(a.slumpCode) - Number(b.slumpCode))
          .filter(
            (slump) =>
              Number(slump.slumpCode) >= slumpProduct.slumpLow && Number(slump.slumpCode) <= slumpProduct.slumpHigh,
          );
        const pumpPrimerSlumpTarget = primerSlumpsList.find(
          (slump) => Number(slump.slumpCode) === slumpProduct.targetSlump,
        );
        const pumpPrimerSlumpMin = primerSlumpsList.find((slump) => Number(slump.slumpCode) === slumpProduct.slumpLow);
        return !!pumpPrimerSlumpTarget.slumpCode ? pumpPrimerSlumpTarget.slumpCode : pumpPrimerSlumpMin.slumpCode;
      }),
    );
  }
}
